import { useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'
import useMenuBack from '../../hooks/useMenuBack'
import { ArrowLeft, Sparkles, Check, X, AlertTriangle, Send, Cloud, ShieldCheck, TrendingUp } from 'lucide-react'
import { useUserStore, useProfileStore } from '../../stores'
import { useI18n } from '../../i18n'
import { aiConsentAPI } from '../../services/api'
import { formatDate } from '../../utils/dateUtils'
import { aiDisclosureBlocks } from '../../utils/aiDisclosure'
import { OrellaSpinner } from '../../components/OrellaLoader' // s123: the app's one spinner

/**
 * AiDataConsentScreen v242.49.0 — Settings → AI & Data Consent.
 *
 * Consent-management surface required by App Store 5.1.1(i)/5.1.2(i): shows what
 * the patient agreed to (the terms date at signup + disclosure version) and lets
 * them withdraw. Because AI is core to Orella, withdrawing consent routes into the
 * existing (vetted) account-deletion flow with the reason preset — no second
 * destructive path. The withdrawal is recorded in the consent ledger first.
 */

const NAVY = '#15354F'
const BLUE = '#46A8F9'
const ICE = '#E9F8FF'
const LBLUE = '#A7D9FC'
const RED = '#E5484D'
const MUTED = '#5B6E80'

export default function AiDataConsentScreen() {
  const { t } = useI18n()
  const navigate = useNavigate()
  const goBack = useMenuBack()
  const user = useUserStore((s) => s.user)
  const profile = useProfileStore((s) => s.profile)

  const [version, setVersion] = useState('')
  // PROD-1225: the date of the patient's latest agreement (the ledger row), from the API's
  // `consentedAt`. Absent on an older API or with no ledger row: the signup date below stays.
  const [consentedAt, setConsentedAt] = useState('')
  const [showConfirm, setShowConfirm] = useState(false)
  const [working, setWorking] = useState(false)

  useEffect(() => {
    let alive = true
    aiConsentAPI.status()
      .then((r) => {
        if (!alive || !r) return
        setVersion(r.consentedVersion || r.currentVersion || '')
        if (r.consented && typeof r.consentedAt === 'string' && r.consentedAt) setConsentedAt(r.consentedAt)
      })
      .catch(() => { /* card just omits the version row */ })
    return () => { alive = false }
  }, [])

  // Date the patient agreed to terms = account creation (signup), not the AI
  // ledger row. Hidden if the field isn't present on the client user/profile.
  const agreedRaw = user?.created_at || user?.createdAt || profile?.created_at || profile?.createdAt || ''
  const signupDate = agreedRaw ? formatDate(agreedRaw) : ''
  const lastAgreedDate = consentedAt ? formatDate(consentedAt) : ''
  const agreedDate = lastAgreedDate || signupDate

  const proceedToDelete = async () => {
    if (working) return
    setWorking(true)
    // Record the withdrawal in the consent ledger before deletion (best-effort —
    // the account deletion is itself the effective withdrawal if this fails).
    try { await aiConsentAPI.withdraw() } catch { /* no-op */ }
    navigate('/settings/delete-account', {
      state: { presetReason: t('aiData.withdrawReason', { defaultValue: 'Withdrawal of AI consent' }) },
    })
  }

  return (
    <div className="min-h-full bg-white flex flex-col">
      {/* Header — matches sibling settings sub-pages (light, own back button) */}
      <div className="sticky-page-header px-4 pt-4 pb-2 flex items-center gap-2.5">
        <button onClick={() => goBack()} style={{ background: 'none', border: 'none' }}>
          <ArrowLeft size={18} color="#64748B" strokeWidth={2.5} />
        </button>
        <span style={{ fontSize: 17, fontWeight: 700, color: NAVY }}>
          {t('aiData.title', { defaultValue: 'AI & Data' })}
        </span>
      </div>

      <div className="flex-1 px-4 pb-10 overflow-auto">
        <p style={{ fontSize: 13, lineHeight: 1.55, color: '#42586C', margin: '6px 2px 14px' }}>
          {t('aiData.intro', { defaultValue: 'Orella uses AI to help prepare your visits — organizing what you share, drafting summaries for your doctor, and answering general questions. Your doctor reviews the output.' })}
        </p>

        {/* Consent record card — ProfileCheckCard style */}
        <div style={{ border: `1.5px solid ${LBLUE}`, borderRadius: 16, overflow: 'hidden', marginBottom: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 9, background: ICE, padding: '11px 13px' }}>
            <div style={{ width: 30, height: 30, borderRadius: 8, background: '#fff', border: '1px solid #E2ECF5', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Sparkles size={15} color={BLUE} strokeWidth={2} />
            </div>
            <span style={{ fontSize: 12.5, fontWeight: 700, color: NAVY }}>
              {t('aiData.consentTitle', { defaultValue: 'Your consent' })}
            </span>
          </div>
          <div style={{ padding: '11px 13px', display: 'flex', flexDirection: 'column', gap: 9 }}>
            {agreedDate && (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 12 }}>
                <span style={{ color: MUTED }}>
                  {lastAgreedDate
                    ? t('aiData.agreedOnDate', { defaultValue: 'Agreed on' })
                    : t('aiData.agreedOn', { defaultValue: 'Agreed at signup' })}
                </span>
                <span style={{ color: NAVY, fontWeight: 600 }}>{agreedDate}</span>
              </div>
            )}
            {version && (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 12 }}>
                <span style={{ color: MUTED }}>{t('aiData.version', { defaultValue: 'Disclosure version' })}</span>
                <span style={{ color: NAVY, fontWeight: 600 }}>{version}</span>
              </div>
            )}
            <p style={{ fontSize: 11.5, color: MUTED, margin: 0, lineHeight: 1.45 }}>
              {lastAgreedDate
                ? t('aiData.consentBodyDated', { defaultValue: 'You agreed to AI-assisted preparation on the date shown above.' })
                : t('aiData.consentBody', { defaultValue: 'You agreed to AI-assisted preparation when you created your account.' })}
            </p>
          </div>
        </div>

        {/* ★★★ v242.153.0 · AI-DISCLOSURE-ONE-COPY-1 — THE DISCLOSURE THE PATIENT
            ACTUALLY AGREED TO, not a summary of it. This block used to be two
            four-word bullets ("Used only to prepare and support your care" /
            "Never used for ads or sold to third parties") on the ONE screen whose
            job is to answer "what did I agree to?". The three blocks below are the
            same array the onboarding consent step renders — see
            utils/aiDisclosure.js for why it is an array and not a second copy. */}
        <div style={{ border: `1.5px solid ${LBLUE}`, borderRadius: 16, overflow: 'hidden', marginBottom: 16 }}>
          {/* ★ surface: 'settings' drops the one onboardingOnly line — "you can change
              your mind in Settings → AI & Data", which on THIS screen names where the
              patient already is, directly above the Withdraw consent button. */}
          {aiDisclosureBlocks(t, { surface: 'settings' }).map((block, bi) => {
            const Icon = { send: Send, cloud: Cloud, shield: ShieldCheck, trending: TrendingUp }[block.icon]
            return (
              <div key={block.id} style={{ display: 'flex', gap: 11, padding: '13px 13px', alignItems: 'flex-start', borderTop: bi === 0 ? 'none' : '1px solid #EDF3F8' }}>
                <div style={{ flexShrink: 0, width: 30, height: 30, borderRadius: 9, background: ICE, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Icon size={16} color={BLUE} strokeWidth={1.9} />
                </div>
                <div>
                  <div style={{ fontSize: 12.5, fontWeight: 700, color: NAVY, marginBottom: 3 }}>{block.title}</div>
                  <div style={{ fontSize: 12, lineHeight: 1.5, color: '#42586C' }}>
                    {block.lines.map((ln, li) => (
                      <span key={li} style={ln.strong ? { color: NAVY, fontWeight: 600 } : undefined}>
                        {ln.text}{li < block.lines.length - 1 ? ' ' : ''}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            )
          })}
        </div>

        {/* The two promises that are NOT in the disclosure blocks and belong to this
            screen: what the data is for, and what it is never sold to. */}
        <ul style={{ listStyle: 'none', margin: '2px 2px 18px', padding: 0 }}>
          {[
            t('aiData.bullet1', { defaultValue: 'Used only to prepare and support your care' }),
            t('aiData.bullet2', { defaultValue: 'Never used for ads or sold to third parties' }),
          ].map((b, i) => (
            <li key={i} style={{ display: 'flex', gap: 9, fontSize: 12.5, lineHeight: 1.5, color: '#42586C', padding: '5px 0' }}>
              <Check size={14} color={BLUE} strokeWidth={2} style={{ flexShrink: 0, marginTop: 2 }} />
              {b}
            </li>
          ))}
        </ul>

        {/* Withdraw */}
        <button
          onClick={() => setShowConfirm(true)}
          style={{ width: '100%', background: '#fff', color: RED, fontSize: 14, fontWeight: 700, border: '1.5px solid rgba(229,72,77,0.35)', borderRadius: 13, padding: 13, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, cursor: 'pointer' }}
        >
          <X size={15} color={RED} strokeWidth={2} />
          {t('aiData.withdraw', { defaultValue: 'Withdraw consent' })}
        </button>
        <p style={{ fontSize: 10.5, color: '#8496A6', textAlign: 'center', marginTop: 9, lineHeight: 1.45, padding: '0 6px' }}>
          {t('aiData.withdrawNote', { defaultValue: "Orella can't run without AI, so withdrawing consent closes and deletes your account." })}
        </p>
      </div>

      {/* Confirm sheet — portal to body (position:fixed inside PageWrapper transform) */}
      {showConfirm && createPortal(
        <div
          onClick={() => !working && setShowConfirm(false)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(21,53,79,0.35)', display: 'flex', alignItems: 'flex-end', zIndex: 1000 }}
        >
          <div onClick={(e) => e.stopPropagation()} style={{ background: '#fff', borderTopLeftRadius: 22, borderTopRightRadius: 22, padding: '20px 18px calc(22px + env(safe-area-inset-bottom, 0px))', width: '100%' }}>
            <div style={{ width: 46, height: 46, borderRadius: 999, background: 'rgba(229,72,77,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px' }}>
              <AlertTriangle size={24} color={RED} strokeWidth={2} />
            </div>
            <h2 style={{ fontSize: 17, fontWeight: 800, color: NAVY, textAlign: 'center', marginBottom: 8 }}>
              {t('aiData.confirmTitle', { defaultValue: 'Withdraw AI consent?' })}
            </h2>
            <p style={{ fontSize: 12.5, lineHeight: 1.55, color: '#51657A', textAlign: 'center', marginBottom: 14 }}>
              {t('aiData.confirmBody', { defaultValue: "Orella uses AI throughout to prepare your care, so it can't run without this consent. Withdrawing deletes your account. You'll confirm with your password next, and there's a short recovery window before it's permanent." })}
            </p>
            <button onClick={proceedToDelete} disabled={working}
              style={{ width: '100%', background: RED, color: '#fff', fontSize: 14, fontWeight: 700, border: 'none', borderRadius: 12, padding: 13, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, opacity: working ? 0.6 : 1, cursor: working ? 'default' : 'pointer' }}>
              {working && <OrellaSpinner size={16} tone="inherit" />}
              {t('aiData.confirmContinue', { defaultValue: 'Continue to delete account' })}
            </button>
            <button onClick={() => !working && setShowConfirm(false)}
              style={{ width: '100%', background: '#fff', color: NAVY, fontSize: 14, fontWeight: 600, border: '1.5px solid #E2E8F0', borderRadius: 12, padding: 12, marginTop: 8, cursor: 'pointer' }}>
              {t('aiData.confirmCancel', { defaultValue: 'Never mind' })}
            </button>
          </div>
        </div>,
        document.body
      )}
    </div>
  )
}
